/**
 * Liquid Template Sample Data and Variable Documentation
 *
 * This file contains the sample data structure and available variables
 * for Order Template Liquid rendering. This mirrors the documentation
 * in app/models/order_template.rb
 *
 */

/**
 * Sample order data for Liquid template preview (i18n versions)
 * This is used in the A4 preview on the Show page
 *
 * NOTE: reserved_barcode_area needs to be loaded dynamically via API
 * Use loadSampleOrderData() function to get complete data with reserved_barcode_area
 */

// Chinese sample data
const SAMPLE_ORDER_DATA_ZH = {
  order: {
    name: "ORD-2025-001",
    extra_name: "INT-2025-001",
    slot_number: 1,
    receiver_name: "王小明",
    phone: "0912-345-678",
    email: "customer@example.com",
    full_address: "台北市信義區信義路五段7號",
    postal_code: "110",
    city: "台北市",
    district: "信義區",
    address: "信義路五段7號",
    country_code: "TW",
    delivery_date: "2025-01-05",
    scheduled_shipping_date: "2025-01-04",
    ordering_date: "2025-01-03",
    created_at: "2025-01-03 10:30:00",
    total_amount: 460.0,
    is_cod: true,
    currency: "TWD",
    delivery_with_pickup: false,
    urgent: false,
    pallet_count: 1,
    note: "請小心輕放",
    invoice_number: "12345678",
    custom_type: "一般訂單",
    seller_name: "測試貨主",
    seller_phone: "02-8888-9999",
    status: "processing",
    shipping_type: { name: "宅配" },
    merged_from_orders: []
  },
  fulfillment: {
    name: "FUL-2025-001",
    shipping_type: { name: "宅配" }
  },
  merchant: {
    name: "測試貨主",
    logo: { url: null }
  },
  sales_channel: {
    name: "官方網站",
    extra_name: "official"
  },
  items: [
    {
      product: {
        sku: "SKU-001",
        name: "測試商品A",
        barcode: "4712345678901",
        size: "M"
      },
      size: "M",
      quantity: 2,
      price: 100.0,
      original_price: 150.0,
      batch: "BATCH-001",
      expiration_date: "2025-12-31",
      inventory_type: { code: "A01" },
      note: "熱銷商品"
    },
    {
      product: {
        sku: "SKU-002",
        name: "測試商品B",
        barcode: "4712345678902",
        size: "L"
      },
      size: "L",
      quantity: 1,
      price: 200.0,
      original_price: 250.0,
      batch: "BATCH-002",
      expiration_date: null,
      inventory_type: { code: "B01" },
      note: ""
    },
    {
      product: {
        sku: "SKU-003",
        name: "測試商品C（組合）",
        barcode: "4712345678903",
        size: "FREE"
      },
      size: "FREE",
      quantity: 1,
      price: 60.0,
      original_price: 80.0,
      batch: null,
      expiration_date: null,
      inventory_type: { code: "A02" },
      note: "",
      bundle: { sku: "BUNDLE-001" }
    }
  ],
  picking_wave_id: 123
};

// English sample data
const SAMPLE_ORDER_DATA_EN = {
  order: {
    name: "ORD-2025-001",
    extra_name: "INT-2025-001",
    slot_number: 1,
    receiver_name: "John Doe",
    phone: "0912-345-678",
    email: "customer@example.com",
    full_address: "No. 7, Sec. 5, Xinyi Rd., Xinyi Dist., Taipei City",
    postal_code: "110",
    city: "Taipei City",
    district: "Xinyi District",
    address: "No. 7, Sec. 5, Xinyi Rd.",
    country_code: "TW",
    delivery_date: "2025-01-05",
    scheduled_shipping_date: "2025-01-04",
    ordering_date: "2025-01-03",
    created_at: "2025-01-03 10:30:00",
    total_amount: 460.0,
    is_cod: true,
    currency: "TWD",
    delivery_with_pickup: false,
    urgent: false,
    pallet_count: 1,
    note: "Please handle with care",
    invoice_number: "12345678",
    custom_type: "Standard Order",
    seller_name: "Test Merchant",
    seller_phone: "02-8888-9999",
    status: "processing",
    shipping_type: { name: "Home Delivery" },
    merged_from_orders: []
  },
  fulfillment: {
    name: "FUL-2025-001",
    shipping_type: { name: "Home Delivery" }
  },
  merchant: {
    name: "Test Merchant",
    logo: { url: null }
  },
  sales_channel: {
    name: "Official Website",
    extra_name: "official"
  },
  items: [
    {
      product: {
        sku: "SKU-001",
        name: "Test Product A",
        barcode: "4712345678901",
        size: "M"
      },
      size: "M",
      quantity: 2,
      price: 100.0,
      original_price: 150.0,
      batch: "BATCH-001",
      expiration_date: "2025-12-31",
      inventory_type: { code: "A01" },
      note: "Best Seller"
    },
    {
      product: {
        sku: "SKU-002",
        name: "Test Product B",
        barcode: "4712345678902",
        size: "L"
      },
      size: "L",
      quantity: 1,
      price: 200.0,
      original_price: 250.0,
      batch: "BATCH-002",
      expiration_date: null,
      inventory_type: { code: "B01" },
      note: ""
    },
    {
      product: {
        sku: "SKU-003",
        name: "Test Product C (Bundle)",
        barcode: "4712345678903",
        size: "FREE"
      },
      size: "FREE",
      quantity: 1,
      price: 60.0,
      original_price: 80.0,
      batch: null,
      expiration_date: null,
      inventory_type: { code: "A02" },
      note: "",
      bundle: { sku: "BUNDLE-001" }
    }
  ],
  picking_wave_id: 123
};

/**
 * Get sample order data by locale (without reserved_barcode_area)
 * @param {string} locale - Language code ('zh' or 'en')
 * @returns {Object} Sample order data
 */
export function getSampleOrderData(locale = 'zh') {
  return locale === 'en' ? { ...SAMPLE_ORDER_DATA_EN } : { ...SAMPLE_ORDER_DATA_ZH };
}

/**
 * Internal function for loadSampleOrderData
 */
function getSampleOrderDataByLocale(locale = 'zh') {
  return locale === 'en' ? SAMPLE_ORDER_DATA_EN : SAMPLE_ORDER_DATA_ZH;
}

/**
 * SAMPLE_ORDER_DATA - For backward compatibility (default to Chinese)
 * Use loadSampleOrderData() for components that need reserved_barcode_area
 */
export const SAMPLE_ORDER_DATA = {
  ...SAMPLE_ORDER_DATA_ZH,
  reserved_barcode_area: '' // Will be loaded via API
};

/**
 * Load complete sample order data with reserved_barcode_area
 * This function fetches the reserved_barcode_area from the backend
 * to ensure frontend and backend use the same HTML structure
 *
 * @param {Object} orderTemplateService - Order template service object
 * @param {string} locale - Language code ('zh' or 'en')
 * @returns {Promise<Object>} Complete sample order data
 */
export async function loadSampleOrderData(orderTemplateService, locale = 'zh') {
  const baseData = getSampleOrderDataByLocale(locale);

  try {
    const response = await orderTemplateService.getSampleReservedBarcodeArea(locale);

    if (response.success && response.data && response.data.html) {
      return {
        ...baseData,
        reserved_barcode_area: response.data.html
      };
    }
    console.warn('[loadSampleOrderData] Failed to load reserved_barcode_area, using empty string');
    return baseData;

  } catch (error) {
    console.error('[loadSampleOrderData] Error loading reserved_barcode_area:', error);
    return baseData;
  }
}

/**
 * Available Liquid Variables Documentation
 * This structure documents all available variables for Liquid templates
 */
export const LIQUID_VARIABLES_DOC = {
  order: {
    description: "訂單基本資訊",
    fields: {
      // 訂單識別（訂單編號取出貨單號 fulfillment.name）
      name: { type: "String", description: "訂單編號", example: "FUL-2025-001", variable: "fulfillment.name" },
      extra_name: { type: "String", description: "額外單號/內部單號", example: "INT-001" },
      slot_number: { type: "Integer", description: "揀貨序號", example: 1 },

      // 收件人資訊
      receiver_name: { type: "String", description: "收件人姓名", example: "王小明" },
      phone: { type: "String", description: "聯絡電話", example: "0912-345-678" },
      email: { type: "String", description: "Email", example: "customer@example.com" },

      // 地址資訊
      full_address: { type: "String", description: "完整地址（組合欄位）", example: "台北市信義區信義路五段7號" },
      postal_code: { type: "String", description: "郵遞區號", example: "110" },
      city: { type: "String", description: "城市", example: "台北市" },
      district: { type: "String", description: "區域", example: "信義區" },
      address: { type: "String", description: "地址", example: "信義路五段7號" },
      country_code: { type: "String", description: "國家代碼", example: "TW" },

      // 日期時間
      delivery_date: { type: "Date", description: "到貨日期", example: "2025-01-03", format: "使用 | date: '%Y-%m-%d'" },
      scheduled_shipping_date: { type: "Date", description: "預定出貨日期", example: "2025-01-02" },
      ordering_date: { type: "Date", description: "訂購日期", example: "2025-01-01" },
      created_at: { type: "DateTime", description: "建立時間", example: "2025-01-01 10:00:00" },

      // 金額資訊
      total_amount: { type: "Decimal", description: "訂單總價", example: 460.0 },
      is_cod: { type: "Boolean", description: "是否貨到付款", example: true },
      currency: { type: "String", description: "幣別", example: "TWD" },

      // 物流資訊（物流方式取出貨單 fulfillment.shipping_type.name）
      "shipping_type.name": { type: "String", description: "物流方式", example: "宅配", variable: "fulfillment.shipping_type.name" },
      delivery_with_pickup: { type: "Boolean", description: "是否來回件", example: false },
      urgent: { type: "Boolean", description: "是否急件", example: false },
      pallet_count: { type: "Integer", description: "棧板數", example: 2 },

      // 其他資訊
      note: { type: "Text", description: "訂單備註", example: "請小心輕放" },
      invoice_number: { type: "String", description: "統一編號", example: "12345678" },
      custom_type: { type: "String", description: "單別/訂單類型", example: "一般訂單" },
      seller_name: { type: "String", description: "賣家名稱", example: "測試賣家" },
      seller_phone: { type: "String", description: "賣家電話", example: "02-1234-5678" },
      status: { type: "String", description: "訂單狀態", example: "processing" }
    }
  },

  merchant: {
    description: "貨主資訊",
    fields: {
      name: { type: "String", description: "貨主名稱", example: "測試貨主" },
      "logo.url": { type: "String", description: "貨主Logo URL", example: "https://example.com/logo.png", note: "用於 <img> 標籤" }
    }
  },

  sales_channel: {
    description: "銷售通路資訊",
    fields: {
      name: { type: "String", description: "通路名稱", example: "蝦皮購物" },
      extra_name: { type: "String", description: "通路內部名稱", example: "shopee" }
    }
  },

  items: {
    description: "訂單商品清單（陣列，需使用 for 迴圈）",
    type: "Array",
    loop_example: "{% for item in items %}{{ item.product.name }}{% endfor %}",
    fields: {
      "product.sku": { type: "String", description: "商品品號", example: "SKU-001" },
      "product.name": { type: "String", description: "商品名稱", example: "測試商品A" },
      "product.barcode": { type: "String", description: "商品條碼", example: "4712345678901" },
      "product.size": { type: "String", description: "商品規格尺寸", example: "M" },
      size: { type: "String", description: "此訂單項目的尺寸（優先於 product.size）", example: "L" },
      quantity: { type: "Integer", description: "訂購數量", example: 2 },
      price: { type: "Decimal", description: "商品單價", example: 100.0 },
      original_price: { type: "Decimal", description: "商品原價", example: 150.0 },
      batch: { type: "String", description: "批號", example: "BATCH-001" },
      expiration_date: { type: "Date", description: "保存期限", example: "2025-12-31" },
      "inventory_type.code": { type: "String", description: "庫存類型代碼", example: "A01" },
      note: { type: "Text", description: "商品備註", example: "限量款" },
      "bundle.sku": { type: "String", description: "組合商品代碼（如為組合商品）", example: "BUNDLE-001" }
    }
  },

  merged_orders: {
    description: "合併訂單資訊（如有合併訂單）",
    type: "Array",
    condition: "{% if order.merged_from_orders %}",
    loop_example: "{% for merged in order.merged_from_orders %}{{ merged.name }}{% endfor %}",
    fields: {
      name: { type: "String", description: "被合併的訂單名稱", example: "ORD-2025-002" },
      extra_name: { type: "String", description: "被合併的訂單內部單號", example: "INT-002" }
    }
  },

  helpers: {
    description: "輔助變數和函數",
    fields: {
      picking_wave_id: { type: "Integer", description: "揀貨單ID", example: 123 },
      current_date: { type: "String", description: "當前日期", example: "{{ 'now' | date: '%Y-%m-%d' }}" },
      current_time: { type: "String", description: "當前時間", example: "{{ 'now' | date: '%H:%M:%S' }}" }
    }
  },

  filters: {
    description: "自定義 Liquid Filters",
    note: "Filters 用於格式化或轉換變數值，使用管道符號 | 連接",
    items: [
      {
        name: "barcode",
        description: "將文字轉換為條碼圖形（SVG）",
        syntax: "{{ value | barcode }}",
        parameters: [
          {
            name: "format",
            type: "String",
            optional: true,
            default: "CODE128",
            description: "條碼格式",
            options: ["CODE128", "CODE39", "EAN13", "EAN8", "UPC"]
          },
          {
            name: "height",
            type: "Integer",
            optional: true,
            default: 50,
            description: "條碼高度（像素）"
          }
        ],
        examples: [
          {
            code: "{{ fulfillment.number | barcode }}",
            description: "使用預設格式 CODE128 生成出貨單條碼"
          },
          {
            code: "{{ fulfillment.number | barcode: 'CODE39' }}",
            description: "使用 CODE39 格式生成條碼"
          },
          {
            code: "{{ fulfillment.number | barcode: 'CODE128', 80 }}",
            description: "使用 CODE128 格式，高度 80px"
          },
          {
            code: "{{ item.product.barcode | barcode }}",
            description: "顯示商品條碼"
          }
        ],
        notes: [
          "條碼會自動顯示條碼值在下方",
          "如果值無效會顯示錯誤訊息",
          "生成的是 SVG 格式，可以完美縮放"
        ]
      }
    ]
  }
};
